// # Revenue sharing calculation utilities
import React from 'react';

// # Team members configuration
export const teamMembers = [
  { id: 'ahmed_hadidy', name: 'Ahmed Elhadidy', email: 'ahmed.hadidyy@gmail.com' },
  { id: 'nabil_mostafa', name: 'Nabil Mostafa', email: 'nabilmostafa14941@gmail.com' },
  { id: 'ahmed_ayman', name: 'Ahmed Ayman', email: 'ahmed.aymaan7474@gmail.com' },
  { id: 'ahmed_hussein', name: 'Ahmed Hussein', email: 'Ahmedsehs77@gmail.com' },
  { id: 'hossam_gomaa', name: 'Hossam Gomaa', email: 'hossamgomaa487@gmail.com' }
];

// # Revenue sharing percentages configuration - EXACTLY as specified
export const revenueShares = {
  // # Library Subscriptions (step1_library, step2_library)
  library: {
    'ahmed_hadidy': 28,
    'ahmed_hussein': 20,
    'ahmed_ayman': 20,
    'hossam_gomaa': 20,
    'nabil_mostafa': 12
  },
  // # Premium Subscriptions (step1_premium, step2_premium)
  premium: {
    'ahmed_hadidy': 22.125,
    'ahmed_hussein': 22.125,
    'ahmed_ayman': 22.125,
    'hossam_gomaa': 22.125,
    'nabil_mostafa': 11.5
  },
  // # Mentorship Monthly (step1_mentorship, step2_mentorship)
  mentorship: {
    egyptian: {
      mentor: 100,      // # Assigned mentor gets 100%
      others: 0     // # Others get 0% each
    },
    international: {
      mentor: 100,      // # Assigned mentor gets 100%
      others: 0    // # Others get 0% each
    }
  },
  // # Tutor Monthly (step1_tutor_hosting, step2_tutor_hosting)
  tutor_hosting: {
    tutor: 50        // # Assigned tutor gets 50%, remaining 50% split equally among team
  }
};

// # Calculate shares based on product type and user nationality
export const calculateShares = (netAmount, product, nationality, mentorId = '', tutorId = '', budgetPercentage = 5) => {
  // # For debugging - log inputs
  console.log('calculateShares inputs:', { netAmount, product, nationality, mentorId, tutorId, budgetPercentage });
  
  // # Initialize shares object
  const shares = {};
  
  // # Validate inputs
  if (!product || !netAmount) {
    console.error('Missing required inputs:', { product, netAmount });
    return shares;
  }
  
  // # Convert product to lowercase for case-insensitive matching
  const productLower = product.toLowerCase();
  
  // # Determine product category
  let productCategory;
  if (productLower.includes('library')) {
    productCategory = 'library';
  } else if (productLower.includes('premium')) {
    productCategory = 'premium';
  } else if (productLower.includes('mentorship') || productLower === 'oet' || productLower === 'us_visa_counselling') {
    productCategory = 'mentorship';
  } else if (productLower.includes('tutor_hosting')) {
    productCategory = 'tutor_hosting';
  } else {
    console.error('Unknown product type:', product);
    return shares;
  }
  
  console.log('Product category determined:', productCategory);
  
  // # Calculate the total amount (if budget was 0%)
  const totalAmount = netAmount / (1 - budgetPercentage / 100);
  
  // # Calculate shares based on product category
  switch(productCategory) {
    case 'library':
      Object.entries(revenueShares.library).forEach(([memberId, percentage]) => {
        // # Calculate net amount (after budget)
        const netShareEgp = netAmount * (percentage/100);
        // # Calculate the total share amount (if budget was 0%)
        const totalShareEgp = totalAmount * (percentage/100);
        
        shares[memberId] = {
          percentage_of_net: percentage,
          net_share_egp: netShareEgp,
          total_share_egp: totalShareEgp // # Total share if budget was 0%
        };
      });
      break;

    case 'premium':
      // # Premium shares - EXACTLY 22.125% each for main team, 11.5% for Nabil
      Object.entries(revenueShares.premium).forEach(([memberId, percentage]) => {
        // # Calculate net amount (after budget)
        const netShareEgp = netAmount * (percentage/100);
        // # Calculate the total share amount (if budget was 0%)
        const totalShareEgp = totalAmount * (percentage/100);
        
        shares[memberId] = {
          percentage_of_net: percentage,
          net_share_egp: netShareEgp,
          total_share_egp: totalShareEgp // # Total share if budget was 0%
        };
      });
      break;

    case 'mentorship':
      // # Determine if user is Egyptian and get correct config
      const isEgyptian = nationality?.toLowerCase() === 'egypt' || 
                        nationality?.toLowerCase() === 'egyptian' ||
                        nationality?.toLowerCase().includes('egypt');
      console.log('Is Egyptian:', isEgyptian, 'Nationality:', nationality);
      
      const mentorshipConfig = isEgyptian ? 
        revenueShares.mentorship.egyptian : 
        revenueShares.mentorship.international;
      
      console.log('Using mentorship config:', mentorshipConfig);
      console.log('Mentor ID:', mentorId);
      
      // # First, assign mentor share if mentor is selected
      if (mentorId) {
        const mentorMember = teamMembers.find(m => m.id === mentorId);
        if (mentorMember) {
          const netShareEgp = netAmount * (mentorshipConfig.mentor/100);
          // # Calculate the total share amount (if budget was 0%)
          const totalShareEgp = totalAmount * (mentorshipConfig.mentor/100);
          
          shares[mentorId] = {
            percentage_of_net: mentorshipConfig.mentor,
            net_share_egp: netShareEgp,
            total_share_egp: totalShareEgp // # Total share if budget was 0%
          };
          console.log('Assigned mentor share:', shares[mentorId]);
        }
      }
      
      // # Then assign others' shares (12.5% each for Egyptian, 16.75% each for international)
      teamMembers.forEach(member => {
        if (member.id !== mentorId) {
          const netShareEgp = netAmount * (mentorshipConfig.others/100);
          // # Calculate the total share amount (if budget was 0%)
          const totalShareEgp = totalAmount * (mentorshipConfig.others/100);
          
          shares[member.id] = {
            percentage_of_net: mentorshipConfig.others,
            net_share_egp: netShareEgp,
            total_share_egp: totalShareEgp // # Total share if budget was 0%
          };
          console.log('Assigned share for', member.name, ':', shares[member.id]);
        }
      });
      
      // # Validate total percentage for mentorship
      const mentorshipTotal = Object.values(shares)
        .reduce((sum, share) => sum + share.percentage_of_net, 0);
      console.log('Total mentorship percentage:', mentorshipTotal);
      
      break;

    case 'tutor_hosting':
      // # Add tutor share (50% of net)
      if (tutorId) {
        const netShareEgp = netAmount * 0.5;
        // # Calculate the total share amount (if budget was 0%)
        const totalShareEgp = totalAmount * 0.5;
        
        shares[tutorId] = {
          percentage_of_net: 50,
          net_share_egp: netShareEgp,
          total_share_egp: totalShareEgp // # Total share if budget was 0%
        };
      }
      
      // # Calculate remaining amount for team members (50% split equally among 5)
      const remainingPercentage = 50; // remaining after tutor's 50%
      const teamMemberPercentage = remainingPercentage / 5; // Split equally among 5 team members
      
      // # Add team member shares
      teamMembers.forEach(member => {
        const netShareEgp = netAmount * (teamMemberPercentage/100);
        // # Calculate the total share amount (if budget was 0%)
        const totalShareEgp = totalAmount * (teamMemberPercentage/100);
        
        shares[member.id] = {
          percentage_of_net: teamMemberPercentage,
          net_share_egp: netShareEgp,
          total_share_egp: totalShareEgp // # Total share if budget was 0%
        };
      });
      break;
  }
  
  // # Log calculated shares for debugging
  console.log('Calculated shares:', shares);
  
  // # Validate total percentage
  const totalPercentage = Object.values(shares)
    .reduce((sum, share) => sum + share.percentage_of_net, 0);
  console.log('Total percentage:', totalPercentage);
  
  return shares;
};

// # Validate share distribution
export const validateShares = (shares) => {
  const totalPercentage = Object.values(shares)
    .reduce((sum, share) => sum + share.percentage_of_net, 0);
  return totalPercentage >= 99 && totalPercentage <= 101;
};

// # Helper function to get team member email
export const getTeamMemberEmail = (memberId) => {
  const member = teamMembers.find(m => m.id === memberId);
  return member ? member.email : '';
};

// # Check if a product has fixed budget percentage (always 5%)
export const isFixedBudgetProduct = (product) => {
  if (!product) return false;
  
  return product.includes('mentorship') || 
         product.includes('tutor_hosting') || 
         product === 'OET' || 
         product === 'US_VISA_Counselling';
};

// # Calculate budget dividend amount and net amount
export const calculateBudgetDividend = (egpTotal, budgetPercentage) => {
  const egpTotalNum = parseFloat(egpTotal) || 0;
  const budgetPercentNum = parseFloat(budgetPercentage) || 0;
  
  const budgetAmount = (egpTotalNum * budgetPercentNum) / 100;
  const netAmount = egpTotalNum - budgetAmount;
  
  return {
    budgetAmount,
    netAmount
  };
};

// # Handle budget dividend change - returns updated form data
export const handleBudgetDividendChange = (formData, newPercentage) => {
  // # Parse values
  const egpTotal = parseFloat(formData.egp_total) || 0;
  const newPercentageNum = parseFloat(newPercentage) || 0;
  
  // # Check if current product is one of the fixed budget products
  const productHasFixedBudget = isFixedBudgetProduct(formData.product);
  
  // # If it's a fixed budget product, keep budget at 5%
  const budgetPercentage = productHasFixedBudget ? 5 : newPercentageNum;
  
  // # Calculate budget amount and net amount
  const { budgetAmount, netAmount } = calculateBudgetDividend(egpTotal, budgetPercentage);
  
  // # Calculate new shares based on net amount
  const newShares = calculateShares(
    netAmount,
    formData.product,
    formData.nationality,
    formData.mentor,
    formData.tutor?.id,
    budgetPercentage // # Pass budget percentage
  );

  // # Return updated form data
  return {
    budget_dvd_percent: budgetPercentage,
    budget_dvd_amount: budgetAmount,
    net_amount_egp: netAmount,
    shares: newShares
  };
};

// # Update form data when product changes
export const handleProductChange = (formData, newProduct) => {
  // # Check if new product is one of the fixed budget products
  const productHasFixedBudget = isFixedBudgetProduct(newProduct);
  
  // # Set budget percentage to 5% for fixed budget products
  const budgetPercentage = productHasFixedBudget ? 5 : formData.budget_dvd_percent;
  
  // # Auto-set duration for fixed duration products
  const newDuration = (newProduct.includes('mentorship') || 
                      newProduct.includes('premium') || 
                      newProduct === 'OET' || 
                      newProduct === 'US_VISA_Counselling' || 
                      newProduct.includes('tutor_hosting')) ? '1 month' : '';
  
  // # Initialize updated form data
  const updatedFormData = {
    duration: newDuration,
    budget_dvd_percent: budgetPercentage
  };
  
  // # Recalculate budget amount and shares if we have the necessary data
  if (formData.egp_total) {
    const { budgetAmount, netAmount } = calculateBudgetDividend(formData.egp_total, budgetPercentage);
    
    updatedFormData.budget_dvd_amount = budgetAmount;
    updatedFormData.net_amount_egp = netAmount;
    updatedFormData.shares = calculateShares(
      netAmount,
      newProduct,
      formData.nationality,
      formData.mentor,
      formData.tutor?.id,
      budgetPercentage // # Pass budget percentage
    );
  }
  
  return updatedFormData;
};

// # Update form data when EGP total changes
export const handleEgpTotalChange = (formData, newEgpTotal) => {
  // # Calculate budget and net amounts
  const { budgetAmount, netAmount } = calculateBudgetDividend(
    newEgpTotal, 
    formData.budget_dvd_percent
  );
  
  // # Calculate new shares based on net amount
  const newShares = calculateShares(
    netAmount,
    formData.product,
    formData.nationality,
    formData.mentor,
    formData.tutor?.id,
    formData.budget_dvd_percent // # Pass budget percentage
  );
  
  // # Return updated form data
  return {
    budget_dvd_amount: budgetAmount.toFixed(2),
    net_amount_egp: netAmount.toFixed(2),
    shares: newShares
  };
};

// # Update shares when nationality changes
export const handleNationalityChange = (formData, newNationality) => {
  // # Only recalculate if we have the necessary data
  if (!formData.egp_total || !formData.product) {
    return {};
  }
  
  // # Calculate net amount
  const netAmount = parseFloat(formData.egp_total) - (parseFloat(formData.budget_dvd_amount) || 0);
  
  // # Calculate new shares
  const newShares = calculateShares(
    netAmount,
    formData.product,
    newNationality,
    formData.mentor,
    formData.tutor?.id,
    formData.budget_dvd_percent // # Pass budget percentage
  );
  
  return { shares: newShares };
};

// # Update shares when mentor changes
export const handleMentorChange = (formData, newMentorId) => {
  // # Only recalculate if we have the necessary data
  if (!formData.egp_total || !formData.product) {
    return {};
  }
  
  // # Calculate net amount
  const netAmount = parseFloat(formData.egp_total) - (parseFloat(formData.budget_dvd_amount) || 0);
  
  // # Calculate new shares
  const newShares = calculateShares(
    netAmount,
    formData.product,
    formData.nationality,
    newMentorId,
    formData.tutor?.id,
    formData.budget_dvd_percent // # Pass budget percentage
  );
  
  return { shares: newShares };
};

// # Format number with commas for thousands
const formatNumber = (num) => {
  if (!num) return '0.00';
  return Number(num).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// # Get display name for recipient
const getRecipientDisplayName = (recipientId) => {
  if (recipientId === 'budget') return 'Budget Allocation';
  const member = teamMembers.find(m => m.id === recipientId);
  return member ? member.name : recipientId;
};

// # Dividend Preview Table Component
export const DividendPreviewTable = React.memo(({ shares, budgetDvdAmount, budgetDvdPercent, egpTotal }) => {
  // # Calculate net amount
  const netAmount = egpTotal - (budgetDvdAmount || 0);

  return (
    <div className="fnc_pay_reg_dividend-preview">
      <h3>Payment Shares Preview</h3>
      <table className="fnc_pay_reg_dividend-table">
        <thead>
          <tr>
            <th>Recipient</th>
            <th>Percentage</th>
            <th>Net Amount (EGP)</th>
            <th>Total Share (EGP)<br/><small>(No Budget)</small></th>
          </tr>
        </thead>
        <tbody>
          {budgetDvdAmount > 0 && (
            <tr className="fnc_pay_reg_budget-row">
              <td>Budget Allocation</td>
              <td>{budgetDvdPercent}% (of total)</td>
              <td>E£ {formatNumber(budgetDvdAmount)}</td>
              <td>E£ 0.00</td>
            </tr>
          )}
          {shares && Object.entries(shares).map(([recipient, data]) => (
            <tr key={recipient}>
              <td>{getRecipientDisplayName(recipient)}</td>
              <td>{data.percentage_of_net}% (of net)</td>
              <td>E£ {formatNumber(data.net_share_egp)}</td>
              <td>E£ {formatNumber(data.total_share_egp)}</td>
            </tr>
          ))}
          <tr className="fnc_pay_reg_net-row">
            <td colSpan="2">Net Amount (after budget)</td>
            <td>E£ {formatNumber(netAmount)}</td>
            <td>-</td>
          </tr>
          <tr className="fnc_pay_reg_total-row">
            <td colSpan="2">Total Amount</td>
            <td>E£ {formatNumber(egpTotal)}</td>
            <td>E£ {formatNumber(egpTotal)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
});
